import type { Condition } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { VaultColors } from '@/theme/colorSchemes';
import { stringToHash } from '@/utils/string';

import type { BackupVault } from './backupVault';

import { PreferencesKey } from '../dal/preferencesKey';

function useBackupVaults(options?: {
  condition?: Condition;
  pageIndex?: number;
  pageSize?: number;
  enabled?: boolean;
}): {
  vaults: BackupVault[];
  totalCount: number | undefined;
  isLoading: boolean;
  refresh: () => void;
} {
  const dal = useDAL();
  const enabled = options?.enabled !== false;
  const {
    isLoading: vaultsLoading,
    data: vaultsData,
    mutate,
  } = dal.vaults.useList(options);

  const {
    data: pref,
    isLoading: prefLoading,
    isValidating: prefValidating,
    mutate: mutatePref,
  } = dal.preferences.useAccountPref(PreferencesKey.BackupVault, enabled);

  const refresh = () => void Promise.all([mutate(), mutatePref()]);

  const vaults = useMemo(
    () =>
      (vaultsData?.vaults || []).map((v) => {
        const userSetting = pref?.value?.colors?.find((p) => p.id === v.id);

        return {
          ...v,
          backgroundColor: userSetting?.color || getDeterministicColor(v.id),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pref?.value?.colors, vaultsData?.vaults, prefValidating]
  );

  return {
    vaults,
    totalCount: vaultsData?.totalCount,
    isLoading: vaultsLoading || prefLoading,
    refresh,
  };
}

export { useBackupVaults };

export function getDeterministicColor(id = 'yo'): string {
  return Object.values(VaultColors)[
    stringToHash(id, Object.values(VaultColors).length)
  ];
}
